import { checkEqual } from '../core/check.ts';
import { clamp, scaledCeiling, scaledFloor, scaledRound } from '../math_core.ts';
import type { ProductOf, Quantity, RatioOf } from './quantity.ts';
import { Proportion } from './quantity.ts';
import type { Scalar } from './scalar.ts';
import { Factory, Unit, type UnitOf } from './unit.ts';

export const Fraction = Unit.create(1, 'fraction', '', Proportion.i);

// Use Measure.scale() instead of .dot() with Proportions.
type DisallowProportion<Q1, Q2, Else extends Quantity> = Q1 extends Proportion
  ? never
  : Q2 extends Proportion
    ? never
    : Else;

export type Dot<Q1 extends Quantity, Q2 extends Quantity> = DisallowProportion<
  Q1,
  Q2,
  // ProductOf<Q1, Q2>
  Q1 extends RatioOf<infer N, Q2> ? N : Q2 extends RatioOf<infer N, Q1> ? N : ProductOf<Q1, Q2>
>;

const add = (a: number, b: number) => a + b;
const subtract = (a: number, b: number) => a - b;
const multiply = (a: number, b: number) => a * b;
const divide = (a: number, b: number) => a / b;
const negate = (a: number) => -a;
const min = (a: number, b: number) => (a < b ? a : b);
const max = (a: number, b: number) => (a > b ? a : b);
// const Invert = (a: number) => 1 / a;

// The measure of a `Quantity` represented as combination of magnitude, dimension, and `Unit`.
// `Measure` provides an API for interacting with such measures, while staying agnostic of their
// representations (i.e. `T`) which govern magnitude and dimension. By definition, values of the
// same quantity and dimension (i.e. same parameterization) can be directly compared.
// TODO: Consider tradeoffs of making this Vector and Scalar a type alias for dimension = 1.
export abstract class Measure<Q extends Quantity, T = unknown> {
  protected constructor(
    readonly units: Unit<Q>,
    readonly raw: T,
  ) {}

  value(units?: Unit<Q>): T {
    units ??= this.units;
    return this.transform(multiply, units.rawToUnits);
  }

  inUnits(units: Unit<Q>) {
    if (units === this.units) {
      return this;
    }
    return this.build(units, this.raw);
  }

  abs() {
    return this.build(this.units, this.transform(Math.abs));
  }

  negate() {
    return this.build(this.units, this.transform(negate));
  }

  plus(quantity: Scalar<Q> | this) {
    return this.build(this.units, this.transform(add, quantity.raw));
  }

  minus(quantity: Scalar<Q> | this) {
    return this.build(this.units, this.transform(subtract, quantity.raw));
  }

  diff(quantity: Scalar<Q> | this) {
    // TODO: Consider optimizing instead of building two Measures.
    return this.minus(quantity).abs();
  }

  atMost(quantity: Scalar<Q> | this) {
    return this.build(this.units, this.transform(min, quantity.raw));
  }

  atLeast(quantity: Scalar<Q> | this) {
    return this.build(this.units, this.transform(max, quantity.raw));
  }

  between(min: Scalar<Q> | this, max: Scalar<Q> | this) {
    return this.build(this.units, this.transform(clamp, min.raw, max.raw));
  }

  floor(units?: Unit<Q>) {
    const scale = (units ?? this.units).rawToUnits;
    return this.build(this.units, this.transform(scale === 1 ? Math.floor : scaledFloor, scale));
  }

  round(units?: Unit<Q>): this {
    const scale = (units ?? this.units).rawToUnits;
    return this.build(this.units, this.transform(scale === 1 ? Math.round : scaledRound, scale));
  }

  ceiling(units?: Unit<Q>) {
    const scale = (units ?? this.units).rawToUnits;
    return this.build(this.units, this.transform(scale === 1 ? Math.ceil : scaledCeiling, scale));
  }

  // TODO: Some of these may not be practically useful. Revisit actual usage after a while.
  // invert() {
  //   return this.build(this.units, this.transform(Invert));
  // }
  times(factor: number | T | Measure<Proportion, number> | Measure<Proportion, T>) {
    // TODO: Consider creating a TypeGuard for Values of a particular Quantity.
    if (factor instanceof Measure) {
      checkEqual(factor.units.quantity, Proportion.i);
      factor = factor.value(Fraction);
    }
    return this.build(this.units, this.transform(multiply, factor));
  }

  dot<Q2 extends Quantity>(quantity: Measure<Q2, number> | Measure<Q2, T>): Measure<Dot<Q, Q2>, T> {
    const units = this.units.dot(quantity.units) as UnitOf<Dot<Q, Q2>>;
    return this.build(units, this.transform(multiply, quantity.raw));
  }

  // Returns the ratio of `this` to `divisor` as a number.
  dividedBy(divisor: Scalar<Q> | this) {
    return this.transform(divide, divisor.raw);
  }

  // Returns the ratio of `this` to `divisor` as a `Measure`.
  per(quantity: Scalar<Q> | this, units?: Unit<Proportion>): Measure<Proportion, T>;
  per<Q2 extends Quantity>(
    quantity: Measure<Q2, number> | Measure<Q2, T>,
  ): Measure<RatioOf<Q, Q2>, T>;
  per<Q2 extends Quantity>(
    quantity: Scalar<Q> | this | Measure<Q2, number> | Measure<Q2, T>,
    units?: Unit<Proportion>,
  ): Measure<Proportion, T> | Measure<RatioOf<Q, Q2>, T> {
    if (quantity.units.quantity === this.units.quantity) {
      return this.build<Proportion>(units ?? Fraction, this.transform(divide, quantity.raw));
    } else {
      const units = this.units.per(quantity.units as Unit<Q2>);
      return this.build(units, this.transform(divide, quantity.raw));
    }
  }

  // Returns a new Measure whose value is a function of `this` (and any arguments).
  map<P extends this[]>(f: (v: T, ...p: T[]) => T, ...params: P): this {
    return this.build(this.units, f(this.raw, ...params.map((p) => p.raw)));
  }

  isEqualTo(other: this) {
    return this.compare(other) === 0;
  }

  isNotEqualTo(other: this) {
    return this.compare(other) !== 0;
  }

  isCloseTo(other: this, { epsilon }: { epsilon: Scalar<Q> }) {
    const lowerBound = other.minus(epsilon);
    const upperBound = other.plus(epsilon);
    return lowerBound.isLessThanOrEqualTo(this) && this.isLessThanOrEqualTo(upperBound);
  }

  isLessThan(other: this) {
    return this.compare(other) < 0;
  }

  isLessThanOrEqualTo(other: this) {
    return this.compare(other) <= 0;
  }

  isGreaterThan(other: this) {
    return this.compare(other) > 0;
  }

  isGreaterThanOrEqualTo(other: this) {
    return this.compare(other) >= 0;
  }

  //
  // The following methods are well-defined but rarely used for scalars.
  //

  // Returns the distance from `this` to the origin.
  // DNS: This is wrong, because rawToUnits needs to be squared
  length() {
    return Factory.build!(this.units, Math.sqrt(this.reduce((acc, q) => acc + q * q, 0)));
  }

  // Uniformly scales length to match `length` if specified. Otherwise, length 1 in `this.units`.
  normalize(length?: Scalar<Q>) {
    length ??= Factory.build!(this.units, 1);
    const factor = length.dividedBy(this.length());
    return this.times(factor);
  }

  // Returns the distance from `this` to `other`.
  distanceBetween(other: this): Scalar<Q> {
    return Factory.build!(
      this.units,
      Math.sqrt(this.minus(other).reduce((acc, q) => acc + q * q, 0)),
    );
  }

  isLengthLessThan(length: Scalar<Q>) {
    const l = length.value(this.units);
    const l2 = l * l;
    const d2 = this.reduce((acc, q) => acc + q * q, 0);
    return d2 < l2;
  }

  isDistanceLessThan(other: this, length: Scalar<Q>) {
    const l = length.value(this.units);
    const l2 = l * l;
    const d2 = this.minus(other).reduce((acc, q) => acc + q * q, 0);
    return d2 < l2;
  }

  // Returns the smallest dimension of `this`.
  minDimension(): Scalar<Q> {
    return Factory.build!(
      this.units,
      this.reduce((acc, q) => Math.min(acc, q), Number.MAX_VALUE),
    );
  }

  // Returns the largest dimension of `this`.
  maxDimension(): Scalar<Q> {
    return Factory.build!(
      this.units,
      this.reduce((acc, q) => Math.max(acc, q), -Number.MAX_VALUE),
    );
  }

  // Performs a pairwise comparison of this.raw with other.raw.
  abstract compare(other: this, fn?: (t: number, o: number) => number): number;

  abstract toString(): string;

  valueOf(): never {
    throw new TypeError(
      `Measure does not allow implicit conversion.` +
        ` Use 'this.value()' instead. Current value: ${this.toString()}`,
    );
  }

  // Constructs a new Quantity of the same type as `this` but with the provided `value` and `units`.
  protected abstract build(units: Unit<Q>, raw: number | T): this;
  protected abstract build<Q2 extends Quantity>(units: Unit<Q2>, raw: number | T): Measure<Q2, T>;

  // Applies transformation `fn` to every element of `this.raw`.
  // If `argA` or `argB` is provided, they are passed pairwise as additional arguments to `fn`.
  protected abstract transform(
    fn: (q: number, a: number, b: number) => number,
    argA?: number | T,
    argB?: number | T,
  ): T;

  // Reduces the dimensions of this quantity to one.
  protected abstract reduce(
    fn: (accumulator: number, q: number) => number,
    initial: number,
  ): number;
}
